/**
 * A collection layer for making permissions play like participants in 8.12
 * @class
 */
import Base from './participants';
import Model from '../models/permissionParticipant';

import GroupsCollection from '@netx/core-user/collections/groups';
import UsersCollection from '@netx/core-user/collections/users';

export default class PermissionsParticipants extends Base {
	/** @override */
	constructor(models, opts = {}) {
		opts.comparator = opts.comparator || 'label';
		opts.model = opts.model || Model;
		// This collection does not have calls to the server
		opts.rpc = false;

		super(models, opts);

		// Debounced/throttled methods
		this.groupsSynced = _.debounce(this.groupsSynced, 50);
		this.labelsUpdated = _.debounce(this.labelsUpdated, 25);
		this.usersSynced = _.debounce(this.usersSynced, 50);

		/**
		 * @memberof module:permissions/collections/permissionsParticipants
		 * @alias permissionsCollection
		 * @type {module:permissions/collections/permissions}
		 */
		this.permissionsCollection = opts.collection;

		/**
		 * Store group models for particpant labeling
		 * @memberof module:permissions/collections/permissionsParticipants
		 * @alias groups
		 * @type {module:user/collections/groups}
		 */
		this.groups = new GroupsCollection();

		/**
		 * Store user models for particpant labeling
		 * @memberof module:permissions/collections/permissionsParticipants
		 * @alias users
		 * @type {module:user/collections/users}
		 */
		this.users = new UsersCollection();

		// Hey, listen!
		this
			// Underlying `userSelector`
			.listenTo(
				this.permissionsCollection,
				'change:userSelector:all',
				this.userSelectorAllToggled,
			)
			.listenTo(
				this.permissionsCollection,
				'userSelector:add:groupIds',
				this.userSelectorAddedGroups,
			)
			.listenTo(
				this.permissionsCollection,
				'userSelector:add:userIds',
				this.userSelectorAddedUsers,
			)
			// Selector object collections
			.listenTo(this.groups, 'sync', this.groupsSynced)
			.listenTo(this.users, 'sync', this.usersSynced)
			// A permission participant's permission has changed
			// We need to reflect that on the underlying userSelector
			.listenTo(this, 'change:permission', (model, permission) => {
				// Remove participant from the old permission
				this.removeParticipant(
					model.get('type'),
					model._previousAttributes.permission,
					model.get('id'),
				);
				// Add to the new permission
				this.addParticipant(model.attributes);
			})
			// A permission participant has been removed
			// We need to reflect that on the underlying userSelector
			.listenTo(this, 'remove', (model, collection, opts) => {
				opts = opts || {};

				// From places like backgrid filter...
				if (!this.permissionsCollection) return;

				this.removeParticipant(
					model.get('type'),
					model.get('permission'),
					model.get('id'),
				);
			});
	}

	/**
	 * Add participants for permission
	 * @method
	 * @param {module:permissions/models/permission} permission Permission
	 */
	addByPermission(permission) {
		const userSelector = permission.get('userSelector');
		const mode = permission.get('mode');
		const toSet = [];

		// I hate wild carding it... but I am not typing it all twice
		_.each(['group', 'user'], (selector) => {
			const ids = userSelector[selector + 'Ids'];

			_.each(ids, (id) => {
				toSet.push({
					uniqueId: selector + '-' + id,
					id: id,
					permission: mode,
					permissionId: permission.id,
					type: selector,
				});
			});

			// Fetch selector objects
			ids.length &&
				this[selector + 's'].getByIds(ids, {
					merge: true,
					remove: false,
				});
		});

		if (userSelector.all === true) {
			toSet.push({
				uniqueId: 'all',
				id: null,
				type: 'all',
				permission: mode,
				permissionId: permission.id,
			});
		}

		return this.set(toSet, {merge: true, remove: false});
	}

	/**
	 * Addidng a participant will modify the underlying userSelector
	 * @override
	 * @param {object} participant Participant object
	 * @param {object} opts Request options
	 */
	addParticipant(participant, opts) {
		// Backgrid...
		if (!this.permissionsCollection) {
			return new $.Deferred().resolveWith(this);
		}

		const collection = this.permissionsCollection;
		let permission = collection.findWhere({mode: participant.permission});

		// Add the permission if there is not already one for this mode
		if (!permission) {
			permission = collection.addPermission({
				mode: participant.permission,
			});
		}

		// Add to the underlying userSelector
		const type = participant.type;
		switch (type) {
			case 'user':
			case 'group':
				permission.addSelectorItems(type, parseInt(participant.id), opts);
				break;
			case 'all':
				permission.toggleSelectAll(true);
				break;
		}

		// Normal pariticpant code gets saved immediately
		return new $.Deferred().resolveWith(this, [permission]);
	}

	/**
	 * @method
	 * @param {module:user/collections/groups} collection Collection
	 */
	groupsSynced(collection) {
		const groups = this.groups;
		if (collection !== groups) return;

		this.filterByType('group').each((m) => {
			const group = groups.get(m.get('id'));
			group && m.set('label', group.getLabel());
		});

		this.labelsUpdated();
	}

	/**
	 * @method
	 */
	labelsUpdated() {
		this.sort();
	}

	/**
	 * Remove participants for permission
	 * @method
	 * @param {module:permissions/models/permission} permission Permission
	 */
	removeByPermission(permission) {
		const userSelector = permission.get('userSelector');
		const mode = permission.get('mode');

		_.each(['group', 'user'], (selector) => {
			var ids = userSelector[selector + 'Ids'];
			_.each(ids, (id) => {
				this.remove(selector + '-' + id);
			});
		});

		if (userSelector.all === true) {
			this.remove('all');
		}
	}

	/**
	 * Removing a participant will modify the underlying userSelector
	 * @override
	 * @param {string} type Selector type (user/group/all)
	 * @param {string} mode Mode (read/write/delete)
	 * @param {number} id Selector id (null for `all`)
	 * @param {object} opts Options
	 */
	removeParticipant(type, mode, id, opts) {
		const permission =
			this.permissionsCollection &&
			this.permissionsCollection.findWhere({mode: mode});

		if (!permission) return new $.Deferred().resolveWith(this);

		switch (type) {
			case 'user':
			case 'group':
				permission.removeSelectorItems(type, id, opts);
				break;
			case 'all':
				permission.toggleSelectAll(false);
				break;
		}

		// Normal pariticpant code gets saved immediately
		return new $.Deferred().resolveWith(this, [permission]);
	}

	/**
	 * The underlying userSelector has changed whether all users are selected or not
	 * We need to reflect that here as a participant
	 * @param {module:permissions/models/permission} Permission
	 * @param {boolean} allSelected All selected
	 * @param {object} opts Options
	 */
	userSelectorAllToggled(permission, allSelected, opts) {
		if (!allSelected) {
			this.remove('all');
			return;
		}

		this.add(
			{
				uniqueId: 'all',
				id: null,
				type: 'all',
				permission: permission.get('mode'),
				permissionId: permission.id,
			},
			{merge: true},
		);
	}

	/**
	 * Groups have been added to the underlying userSelector
	 * We need to reflect that here as a participants
	 * @method
	 * @param {module:permissions/models/permission} Permission
	 * @param {array} groupIds Added group ids
	 * @param {object} opts Options
	 */
	userSelectorAddedGroups(permission, ids, opts) {
		const mode = permission.get('mode');
		this.set(
			ids.map((id) => {
				return {
					uniqueId: 'group-' + id,
					id: id,
					permission: mode,
					permissionId: permission.id,
					type: 'group',
				};
			}),
			{
				merge: true,
				remove: false,
			},
		);
		this.groups.getByIds(ids);
	}

	/**
	 * Users have been added to the underlying userSelector
	 * We need to reflect that here as a participants
	 * @method
	 * @param {module:permissions/models/permission} Permission
	 * @param {array} ids Added user ids
	 * @param {object} opts Options
	 */
	userSelectorAddedUsers(permission, ids, opts) {
		const mode = permission.get('mode');
		this.set(
			ids.map((id) => {
				return {
					uniqueId: 'user-' + id,
					id: id,
					permission: mode,
					permissionId: permission.id,
					type: 'user',
				};
			}),
			{
				merge: true,
				remove: false,
			},
		);
		this.users.getByIds(ids);
	}

	/**
	 * @method
	 * @param {module:user/collections/users} collection Collection
	 */
	usersSynced(collection) {
		const users = this.users;
		if (collection !== users) return;

		this.filterByType('user').each((m) => {
			const user = users.get(m.get('id'));
			user && m.set('label', user.getLexicographicName());
		});
		this.labelsUpdated();
	}

	/** @type {string} */
	static get __name__() {
		return 'PermissionsParticipants';
	}
}
